import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getShipmentConfigs, updateAllShipmentConfigs, updateShipmentConfig } from '../config/api';
import './Shipments.css';

const RULE_LABELS = { panels: 'Panel', inverters: 'Inverter', batteries: 'Battery', bos: 'BOS' };

const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('userData_30jan') || '{}'); }
  catch (_) { return {}; }
};

const editableConfig = (config) => ({
  enabled: config.enabled,
  twoWheelerEnabled: config.twoWheelerEnabled,
  twoWheelerMaxDistanceKm: config.twoWheelerMaxDistanceKm,
  capacityPoints: config.capacityPoints,
  twoWheelerCapacityPoints: config.twoWheelerCapacityPoints ?? config.capacityPoints,
  maxOrdersPerShipment: config.maxOrdersPerShipment,
  maxStopsPerShipment: config.maxStopsPerShipment,
  loadRules: (config.loadRules || []).map(rule => ({ ...rule })),
});

const configPayload = (config, includeEnabled = true) => ({
  ...(includeEnabled ? { enabled: Boolean(config.enabled) } : {}),
  twoWheelerEnabled: Boolean(config.twoWheelerEnabled),
  twoWheelerMaxDistanceKm: Number(config.twoWheelerMaxDistanceKm),
  capacityPoints: Number(config.capacityPoints),
  twoWheelerCapacityPoints: Number(config.twoWheelerCapacityPoints),
  maxOrdersPerShipment: Number(config.maxOrdersPerShipment),
  maxStopsPerShipment: Number(config.maxStopsPerShipment),
  loadRules: config.loadRules.map(rule => ({ ...rule, pointsPerUnit: Number(rule.pointsPerUnit) })),
});

const configSignature = (config) => JSON.stringify(configPayload(config));

const ShipmentConfig = () => {
  const isSuperAdmin = getCurrentUser().role === 'superAdmin';
  const [configs, setConfigs] = useState([]);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [showApplyAllConfirm, setShowApplyAllConfirm] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadConfigs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await getShipmentConfigs();
      const data = response.data || [];
      setConfigs(data);
      setSavedConfigs(data);
      if (data.length) setGlobalConfig(editableConfig(data[0]));
    } catch (requestError) {
      setError(requestError.message || 'Could not load shipment configuration');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const dirtyWarehouseIds = useMemo(() => new Set(configs.filter(config => {
    const saved = savedConfigs.find(item => item.warehouseId === config.warehouseId);
    return !saved || configSignature(config) !== configSignature(saved);
  }).map(config => config.warehouseId)), [configs, savedConfigs]);

  const updateField = (warehouseId, field, value) => {
    setConfigs(current => current.map(config => config.warehouseId === warehouseId ? { ...config, [field]: value } : config));
  };

  const updateRule = (warehouseId, code, value) => {
    setConfigs(current => current.map(config => config.warehouseId !== warehouseId ? config : {
      ...config,
      loadRules: config.loadRules.map(rule => rule.code === code ? { ...rule, pointsPerUnit: value } : rule),
    }));
  };

  const updateGlobalField = (field, value) => setGlobalConfig(current => ({ ...current, [field]: value }));
  const updateGlobalRule = (code, value) => setGlobalConfig(current => ({
    ...current,
    loadRules: current.loadRules.map(rule => rule.code === code ? { ...rule, pointsPerUnit: value } : rule),
  }));

  const saveConfig = async (config) => {
    setSavingId(config.warehouseId);
    setError('');
    setNotice('');
    try {
      await updateShipmentConfig(config.warehouseId, configPayload(config));
      setNotice(`${config.warehouse} saved. The new vehicle load targets apply to future shipments.`);
      await loadConfigs({ silent: true });
    } catch (requestError) {
      setError(requestError.message || 'Could not save shipment configuration');
    } finally {
      setSavingId(null);
    }
  };

  const saveAllConfigs = async () => {
    setSavingAll(true);
    setError('');
    setNotice('');
    try {
      const response = await updateAllShipmentConfigs(configPayload(globalConfig, false));
      const updatedCount = response.data?.updatedCount || configs.length;
      setShowApplyAllConfirm(false);
      setNotice(`Configuration applied successfully to ${updatedCount} active hubs.`);
      await loadConfigs({ silent: true });
    } catch (requestError) {
      setError(requestError.message || 'Could not apply configuration to all hubs');
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <div className="container shipment-config-page">
      <div className="shipment-detail-breadcrumb"><Link to="/shipments">Shipments</Link><span>/</span><strong>Configuration</strong></div>
      <div className="page-header shipments-header shipment-config-page-header">
        <div><h1 className="page-title">Vehicle Configuration</h1><p className="page-subtitle">{isSuperAdmin ? 'Manage truck and bike load targets globally or fine-tune an individual hub' : 'Manage truck and bike load targets for your assigned hubs'}</p></div>
        <span className="shipments-run-note">Only future shipments are affected</span>
      </div>

      {error && <div className="shipment-api-error">{error}</div>}
      {notice && <div className="shipment-config-notice">{notice}</div>}

      {loading ? <div className="card shipments-empty">Loading configuration…</div> : <>
        {isSuperAdmin && globalConfig && (
          <section className="card shipment-config-global">
            <div className="shipment-config-global-header">
              <div className="shipment-config-global-title">
                <span className="shipment-config-global-icon">ALL</span>
                <div><h2>Configuration for all hubs</h2><p>Use one setup across every active hub. This replaces their individual values.</p></div>
              </div>
              <div className="shipment-config-global-meta"><strong>{configs.length}</strong><span>active hubs</span></div>
            </div>

            <div className="shipment-config-global-body">
              <div className="shipment-config-global-primary">
                <FourWheelerSettings
                  config={globalConfig}
                  onCapacityChange={value => updateGlobalField('capacityPoints', value)}
                  onOrdersChange={value => updateGlobalField('maxOrdersPerShipment', value)}
                  onStopsChange={value => updateGlobalField('maxStopsPerShipment', value)}
                />
                <TwoWheelerSettings config={globalConfig} onToggle={value => updateGlobalField('twoWheelerEnabled', value)} onCapacityChange={value => updateGlobalField('twoWheelerCapacityPoints', value)} onDistanceChange={value => updateGlobalField('twoWheelerMaxDistanceKm', value)} />
              </div>
              <div className="shipment-config-global-rules">
                <h3>Load points per unit</h3>
                <div className="shipment-config-rule-grid">
                  {globalConfig.loadRules.filter(rule => RULE_LABELS[rule.code]).map(rule => (
                    <ConfigNumber key={rule.code} label={RULE_LABELS[rule.code]} value={rule.pointsPerUnit} min="0" step="0.1" onChange={value => updateGlobalRule(rule.code, value)} />
                  ))}
                </div>
              </div>
            </div>

            <div className="shipment-config-global-actions">
              <p>Existing shipments keep their original vehicle and load-target snapshot.</p>
              <button type="button" className="btn btn-primary shipment-save-all-button" disabled={savingAll} onClick={() => setShowApplyAllConfirm(true)}>Apply and save for all hubs</button>
            </div>
          </section>
        )}

        <div className="shipment-config-section-heading">
          <div><h2>{isSuperAdmin ? 'Hub-specific overrides' : 'Assigned hub settings'}</h2><p>{isSuperAdmin ? 'Adjust and save a single hub without changing any other location.' : 'Only hubs assigned to your Delivery Manager account are available here.'}</p></div>
          <span>{configs.length} hubs</span>
        </div>

        <div className="shipment-config-grid">
          {configs.map(config => {
            const isDirty = dirtyWarehouseIds.has(config.warehouseId);
            return (
              <div className={`card shipment-config-card ${!config.enabled ? 'shipment-config-card-disabled' : ''}`} key={config.warehouseId}>
                <div className="shipment-config-card-header">
                  <div><h2>{config.warehouse}</h2><span>Hub ID #{config.warehouseId}</span></div>
                  <label className="shipment-config-toggle"><input type="checkbox" checked={config.enabled} onChange={event => updateField(config.warehouseId, 'enabled', event.target.checked)} /><span>{config.enabled ? 'Enabled' : 'Disabled'}</span></label>
                </div>
                <FourWheelerSettings
                  config={config}
                  onCapacityChange={value => updateField(config.warehouseId, 'capacityPoints', value)}
                  onOrdersChange={value => updateField(config.warehouseId, 'maxOrdersPerShipment', value)}
                  onStopsChange={value => updateField(config.warehouseId, 'maxStopsPerShipment', value)}
                />
                <TwoWheelerSettings config={config} onToggle={value => updateField(config.warehouseId, 'twoWheelerEnabled', value)} onCapacityChange={value => updateField(config.warehouseId, 'twoWheelerCapacityPoints', value)} onDistanceChange={value => updateField(config.warehouseId, 'twoWheelerMaxDistanceKm', value)} />
                <h3>Load points per unit</h3>
                <div className="shipment-config-rule-grid">
                  {config.loadRules.filter(rule => RULE_LABELS[rule.code]).map(rule => (
                    <ConfigNumber key={rule.code} label={RULE_LABELS[rule.code]} value={rule.pointsPerUnit} min="0" step="0.1" onChange={value => updateRule(config.warehouseId, rule.code, value)} />
                  ))}
                </div>
                <div className="shipment-config-card-actions">
                  <span className={isDirty ? 'shipment-config-unsaved' : 'shipment-config-saved'}>{isDirty ? 'Unsaved changes' : 'Up to date'}</span>
                  <button type="button" className="btn btn-secondary" disabled={!isDirty || savingId === config.warehouseId || savingAll} onClick={() => saveConfig(config)}>{savingId === config.warehouseId ? 'Saving…' : 'Save this hub'}</button>
                </div>
              </div>
            );
          })}
        </div>
      </>}

      {isSuperAdmin && showApplyAllConfirm && <div className="shipment-start-overlay" role="presentation" onMouseDown={() => !savingAll && setShowApplyAllConfirm(false)}><div className="shipment-start-dialog shipment-config-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="apply-all-config-title" onMouseDown={event => event.stopPropagation()}><div className="shipment-start-dialog-header"><div><h2 id="apply-all-config-title">Apply configuration to all hubs?</h2><p>{configs.length} active hubs will receive the same vehicle settings</p></div><button type="button" aria-label="Close" disabled={savingAll} onClick={() => setShowApplyAllConfirm(false)}>×</button></div><div className="shipment-config-confirm-summary"><span>Truck target <strong>{globalConfig?.capacityPoints} points</strong></span><span>Bike target <strong>{globalConfig?.twoWheelerCapacityPoints} points</strong></span><span>Orders <strong>{globalConfig?.maxOrdersPerShipment} max</strong></span><span>Stops <strong>{globalConfig?.maxStopsPerShipment} max</strong></span><span>Two-wheeler <strong>{globalConfig?.twoWheelerEnabled ? `Up to ${globalConfig.twoWheelerMaxDistanceKm} km` : 'Disabled'}</strong></span></div><p className="shipment-config-confirm-copy">Each hub keeps its current Enabled/Disabled setting. Existing shipments will not change.</p><div className="shipment-start-dialog-actions"><button type="button" className="btn btn-secondary" disabled={savingAll} onClick={() => setShowApplyAllConfirm(false)}>Cancel</button><button type="button" className="btn btn-primary" disabled={savingAll} onClick={saveAllConfigs}>{savingAll ? 'Applying…' : `Apply to ${configs.length} hubs`}</button></div></div></div>}
    </div>
  );
};

const ConfigNumber = ({ label, suffix, value, onChange, ...inputProps }) => <label className="shipment-config-field"><span>{label}</span><input type="number" className="form-control" value={value} onChange={event => onChange(event.target.value)} {...inputProps} />{suffix && <small>{suffix}</small>}</label>;

const FourWheelerSettings = ({ config, onCapacityChange, onOrdersChange, onStopsChange }) => <section className="shipment-four-wheeler-config"><div className="shipment-two-wheeler-copy"><div><span className="shipment-vehicle-icon shipment-four-wheeler-icon">4W</span><div><strong>Four-wheeler delivery</strong><small>Primary vehicle for panels, larger orders and consolidated loads</small></div></div><span className="shipment-vehicle-enabled-badge">Always available</span></div><div className="shipment-vehicle-settings-grid shipment-four-wheeler-settings-grid"><ConfigNumber label="Load target" suffix="planning points" value={config.capacityPoints} min="1" step="0.1" onChange={onCapacityChange} /><ConfigNumber label="Maximum orders" suffix="per shipment" value={config.maxOrdersPerShipment} min="1" max="500" onChange={onOrdersChange} /><ConfigNumber label="Route stops" suffix="maximum 25" value={config.maxStopsPerShipment} min="1" max="25" onChange={onStopsChange} /></div></section>;

const TwoWheelerSettings = ({ config, onToggle, onCapacityChange, onDistanceChange }) => <section className={`shipment-two-wheeler-config ${config.twoWheelerEnabled ? '' : 'shipment-two-wheeler-disabled'}`}><div className="shipment-two-wheeler-copy"><div><span className="shipment-vehicle-icon">2W</span><div><strong>Two-wheeler delivery</strong><small>Single inverter, battery or BOS unit only · panels excluded</small></div></div><label className="shipment-config-toggle"><input type="checkbox" checked={Boolean(config.twoWheelerEnabled)} onChange={event => onToggle(event.target.checked)} /><span>{config.twoWheelerEnabled ? 'Enabled' : 'Disabled'}</span></label></div><div className="shipment-vehicle-settings-grid"><ConfigNumber label="Load target" suffix="planning points" value={config.twoWheelerCapacityPoints} min="1" step="0.1" disabled={!config.twoWheelerEnabled} onChange={onCapacityChange} /><ConfigNumber label="Maximum delivery distance" suffix="from source hub" value={config.twoWheelerMaxDistanceKm} min="1" max="200" step="1" disabled={!config.twoWheelerEnabled} onChange={onDistanceChange} /></div></section>;

export default ShipmentConfig;
